import { MouseEventHandler, ReactElement, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import BigNumber from 'bignumber.js';
import { Address } from 'viem';

import { IpfsImage } from 'modules/common/components/IpfsImage';
import { DECIMAL_PLACES_SHORT } from 'modules/common/const';
import { MinusIcon, PlusIcon } from 'modules/common/icons';
import { cropString } from 'modules/common/utils/cropString';
import { KnownDialogs, useDialog } from 'modules/dialogs';
import { useGlobalTranslation } from 'modules/i18n';
import { PoolRoutesConfig } from 'modules/pool/Routes';

import { StakingTable as Table } from '../StakingTable';
import { translation } from './translation';
import { useStyles } from './useStyles';

interface ITableRowProps {
  imageIpfsLink?: string;
  poolName?: string;
  apy?: string | number;
  amount?: BigNumber;
  amountUsd?: BigNumber;
  poolAddress: Address;
  isLoading?: boolean;
}

export function TableRow({
  imageIpfsLink,
  poolName,
  apy,
  amount,
  amountUsd,
  poolAddress,
  isLoading,
}: ITableRowProps): ReactElement {
  const { keys, t } = useGlobalTranslation(translation);
  const { classes } = useStyles();

  const poolLink = PoolRoutesConfig.pool.generatePath(poolAddress);

  const { onOpen: onDelegateOpen } = useDialog<string>(KnownDialogs.delegate);

  const { onOpen: onWithdrawOpen } = useDialog<string>(KnownDialogs.withdraw);

  const onPlusClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      event.stopPropagation();
      onDelegateOpen(poolAddress);
    },
    [onDelegateOpen, poolAddress],
  );

  const onMinusClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      event.stopPropagation();
      onWithdrawOpen(poolAddress);
    },
    [onWithdrawOpen, poolAddress],
  );

  const poolCell = (
    <Table.Cell>
      <Stack
        alignItems="center"
        component={Link}
        direction="row"
        spacing={3}
        sx={{ mb: { xs: 4, md: 0 }, color: 'inherit' }}
        to={poolLink}
      >
        <div className={classes.imageContainer}>
          <IpfsImage className={classes.image} ipfsLink={imageIpfsLink} />
        </div>

        <Stack>
          <Typography>
            {!poolName || isLoading ? <Skeleton width={60} /> : poolName}
          </Typography>

          <Typography color="text.secondary" variant="body2">
            {!poolAddress || isLoading ? (
              <Skeleton width={80} />
            ) : (
              cropString(poolAddress)
            )}
          </Typography>
        </Stack>
      </Stack>
    </Table.Cell>
  );

  const apyCell = (
    <Table.Cell mdLabel={t(keys.apy)}>
      {isLoading ? (
        <Skeleton width={40} />
      ) : (
        t(keys.unit.percent, { value: apy })
      )}
    </Table.Cell>
  );

  const myPositionCell = (
    <Table.Cell mdLabel={t(keys.myPosition)}>
      <Stack alignItems={{ xs: 'end', md: 'start' }}>
        <Typography>
          {isLoading || !amount ? (
            <Skeleton width={60} />
          ) : (
            t(keys.unit.tokenValue, {
              value: amount.decimalPlaces(DECIMAL_PLACES_SHORT).toFormat(),
              token: t(keys.tokens.main),
            })
          )}
        </Typography>

        <Typography color="text.secondary" variant="body2">
          {isLoading ? (
            <Skeleton width={80} />
          ) : (
            amountUsd &&
            t(keys.unit.$, {
              value: amountUsd.toFormat(DECIMAL_PLACES_SHORT),
            })
          )}
        </Typography>
      </Stack>
    </Table.Cell>
  );

  const actionsCell = (
    <Table.Cell>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          display: { md: 'none' },
          width: '100%',
          mt: 3,
        }}
      >
        <Button
          fullWidth
          color="secondary"
          disabled={isLoading}
          variant="outlined"
          onClick={onMinusClick}
        >
          {t(keys.withdraw)}
        </Button>

        <Button
          fullWidth
          color="primary"
          disabled={isLoading}
          variant="outlined"
          onClick={onPlusClick}
        >
          {t(keys.delegateMore)}
        </Button>
      </Stack>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={2}
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: '100%',
        }}
      >
        <Tooltip placement="top" title={t(keys.withdraw)}>
          <Button
            className={classes.iconButton}
            color="secondary"
            disabled={isLoading}
            variant="outlined"
            onClick={onMinusClick}
          >
            <MinusIcon />
          </Button>
        </Tooltip>

        <Tooltip placement="top" title={t(keys.delegateMore)}>
          <Button
            className={classes.iconButton}
            color="primary"
            disabled={isLoading}
            variant="outlined"
            onClick={onPlusClick}
          >
            <PlusIcon />
          </Button>
        </Tooltip>
      </Stack>
    </Table.Cell>
  );

  return (
    <Table.Row>
      {poolCell}

      {apyCell}

      {myPositionCell}

      {actionsCell}
    </Table.Row>
  );
}
